import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  EyeOff,
  Image as ImageIcon,
  Loader2,
  RefreshCcw,
  RotateCcw,
  Sparkles,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import * as api from '@/api/client';
import type { MatterFigure } from '@/api/client';

interface FiguresTabProps {
  matterId: string;
  isFrozen: boolean;
  /** Hoisted so the parent (Stage1.tsx) can drive the TabBar badge
   *  count without having to also fetch /figures itself. */
  onCountChange: (count: number) => void;
  onError: (msg: string | null) => void;
  /** Integer token the parent bumps when new figures may have landed
   *  on the server (e.g. a second document finished indexing via the
   *  matter_documents poller). Bumping re-runs the /figures fetch so
   *  the grid picks up the new rows without a tab-switch or manual
   *  refresh. Undefined on older call sites = no-op. */
  reloadToken?: number;
}

// EMF/WMF land in matter_figures with the original mime type even
// when the upstream pipeline subbed in a placeholder PNG, because we
// don't want to lie about the source format. The visual cue ("vision
// can only describe position") is keyed off that, so the bucket
// mirrors the placeholder logic in backend/file_parser.py.
const _PLACEHOLDER_MIMES = new Set([
  'image/x-emf',
  'image/x-wmf',
  'image/emf',
  'image/wmf',
  'application/octet-stream',
]);

type FigureBucket = 'analysed' | 'placeholder' | 'pending';

function _figureBucket(f: MatterFigure): FigureBucket {
  const mime = (f.mime_type || '').toLowerCase();
  // An EMF/WMF with a rendered preview PNG is effectively a
  // raster — vision sees real pixels, the UI shows a real
  // thumbnail. Don't bucket it under "placeholder" just because
  // the source mime is a vector; has_preview is the truthier
  // signal for the UI treatment.
  const isPlaceholder = _PLACEHOLDER_MIMES.has(mime) && !f.has_preview;
  const hasDescription = !!(f.description || '').trim();
  if (isPlaceholder) return 'placeholder';
  if (hasDescription) return 'analysed';
  return 'pending';
}

interface UndoState {
  figure: MatterFigure;
  expiresAt: number;
}

interface BatchProgress {
  current: number;       // 1-based index of figure currently being processed
  total: number;
  failed: number;
}

const DESCRIPTION_TRUNCATE_AT = 200;
const DESCRIPTION_TRUNCATE_TO = 150;

export default function FiguresTab({
  matterId, isFrozen, onCountChange, onError, reloadToken,
}: FiguresTabProps) {
  const [figures, setFigures] = useState<MatterFigure[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Record<number, boolean>>({});
  const [undoState, setUndoState] = useState<UndoState | null>(null);

  // Per-figure analyse state. `analysing` is the spinner gate;
  // `analyseFailed` flips the button label to "Failed — retry" so a
  // transient error doesn't disappear into the toast and a re-click
  // does the right thing. `expandedDesc` drives the "Show more"
  // toggle for descriptions over DESCRIPTION_TRUNCATE_AT chars.
  const [analysing, setAnalysing] = useState<Record<number, boolean>>({});
  const [analyseFailed, setAnalyseFailed] = useState<Record<number, boolean>>({});
  const [expandedDesc, setExpandedDesc] = useState<Record<number, boolean>>({});
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(null);
  // Track in-flight include/exclude toggles so a double-click doesn't
  // fire two PATCHes against the same figure. We optimistically flip
  // the row in `figures` immediately so the checkbox feels live;
  // failure rolls it back via onError + a refresh.
  const [pendingToggle, setPendingToggle] = useState<Record<number, boolean>>({});

  // Reload from /api/matter/{id}/figures whenever the tab opens (i.e.
  // on first mount). The parent badge count is hoisted via
  // onCountChange so the TabBar number stays in sync without a
  // duplicate fetch in Stage1.tsx.
  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const resp = await api.listMatterFigures(matterId);
      setFigures(resp.figures);
      onCountChange(resp.figure_count);
    } catch (e: any) {
      onError(e?.response?.data?.detail || 'Failed to load figures');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [matterId, onCountChange, onError]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId, reloadToken]);

  // Undo timer — 5 s window after a discard. We keep the deleted
  // figure in `undoState` (NOT in the visible grid) and re-POST the
  // figure on Undo via re-fetch + status flag. Clearing the timer on
  // unmount avoids a setState after unmount when the drafter
  // navigates away mid-window.
  const undoTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (undoTimerRef.current !== null) {
      window.clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
    if (!undoState) return;
    const ms = Math.max(0, undoState.expiresAt - Date.now());
    undoTimerRef.current = window.setTimeout(() => {
      setUndoState(null);
      undoTimerRef.current = null;
    }, ms);
    return () => {
      if (undoTimerRef.current !== null) {
        window.clearTimeout(undoTimerRef.current);
        undoTimerRef.current = null;
      }
    };
  }, [undoState]);

  // Single-figure analyse. Updates the row's description in place on
  // success so the card switches to Analysed without a refresh
  // round-trip. Re-analyse just calls the same endpoint — backend
  // overwrites matter_figures.description.
  const analyseFigure = useCallback(async (figureId: number): Promise<boolean> => {
    setAnalysing((prev) => ({ ...prev, [figureId]: true }));
    setAnalyseFailed((prev) => {
      const next = { ...prev };
      delete next[figureId];
      return next;
    });
    try {
      const resp = await api.analyseMatterFigure(matterId, figureId);
      setFigures((prev) => (prev || []).map(
        (f) => (f.id === figureId
          ? { ...f, description: resp.description, included_in_context: resp.included_in_context }
          : f),
      ));
      return true;
    } catch (e: any) {
      onError(e?.response?.data?.detail || `Vision analysis failed for figure ${figureId}`);
      setAnalyseFailed((prev) => ({ ...prev, [figureId]: true }));
      return false;
    } finally {
      setAnalysing((prev) => {
        const next = { ...prev };
        delete next[figureId];
        return next;
      });
    }
  }, [matterId, onError]);

  // Sequential analyse-all over every figure that doesn't already
  // have a description. Sequential (not parallel) because vision
  // models commonly rate-limit at low RPS and a 7-image parallel
  // burst trips that on shared API keys. Progress updates in a
  // status strip; errors don't abort — they just bump `failed` so
  // the drafter sees how many slipped.
  const handleAnalyseAll = async () => {
    const all = figures || [];
    const targets = all.filter((f) => !((f.description || '').trim()));
    if (targets.length === 0 || batchProgress) return;
    setBatchProgress({ current: 0, total: targets.length, failed: 0 });
    let failed = 0;
    for (let i = 0; i < targets.length; i += 1) {
      setBatchProgress({ current: i + 1, total: targets.length, failed });
      // eslint-disable-next-line no-await-in-loop
      const ok = await analyseFigure(targets[i].id);
      if (!ok) failed += 1;
    }
    setBatchProgress(null);
  };

  const handleDiscard = async (figure: MatterFigure) => {
    if (isFrozen) return;
    setPendingDelete((prev) => ({ ...prev, [figure.id]: true }));
    try {
      await api.deleteMatterFigure(matterId, figure.id);
      setFigures((prev) => {
        const next = (prev || []).filter((f) => f.id !== figure.id);
        onCountChange(next.length);
        return next;
      });
      setUndoState({ figure, expiresAt: Date.now() + 5000 });
    } catch (e: any) {
      onError(e?.response?.data?.detail || `Failed to discard ${figure.fig_number}`);
    } finally {
      setPendingDelete((prev) => {
        const next = { ...prev };
        delete next[figure.id];
        return next;
      });
    }
  };

  const handleUndo = async () => {
    setUndoState(null);
    await refresh();
  };

  // Include/exclude toggle. Optimistic — flip the row first so the
  // checkbox responds instantly, fire the PATCH, roll back via
  // refresh on failure. The backend PATCH endpoint returns the
  // updated row but we ignore it (the optimistic copy is already
  // correct on success); a refresh is only needed on the failure
  // path so we don't end up showing stale state.
  const handleToggleInclude = async (figure: MatterFigure, next: boolean) => {
    if (isFrozen || pendingToggle[figure.id]) return;
    setPendingToggle((prev) => ({ ...prev, [figure.id]: true }));
    setFigures((prev) => (prev || []).map(
      (f) => (f.id === figure.id ? { ...f, included_in_context: next } : f),
    ));
    try {
      await api.patchMatterFigure(matterId, figure.id, { included_in_context: next });
    } catch (e: any) {
      onError(e?.response?.data?.detail || `Failed to ${next ? 'include' : 'exclude'} ${figure.fig_number}`);
      // Roll back to ground truth on failure.
      await refresh();
    } finally {
      setPendingToggle((prev) => {
        const out = { ...prev };
        delete out[figure.id];
        return out;
      });
    }
  };

  // Header counts. Computed over the latest `figures` array so the
  // header updates immediately on discard or analyse without waiting
  // for a refresh round-trip. "Not analysed" lumps placeholder +
  // pending so the wording matches the spec ("3 analysed · 4 not
  // analysed") regardless of source format.
  const counts = useMemo(() => {
    const all = figures || [];
    let analysed = 0;
    let placeholder = 0;
    let pending = 0;
    let inContext = 0;
    let excluded = 0;
    for (const f of all) {
      const b = _figureBucket(f);
      if (b === 'analysed') analysed += 1;
      else if (b === 'placeholder') placeholder += 1;
      else pending += 1;
      // A figure is "in context" only when it has a description AND
      // is flagged included. Same predicate as build_idf_context's
      // SQL filter so the header number matches what the backend
      // actually feeds the LLM. Pending-no-description figures count
      // as excluded for header purposes — their checkbox state is
      // moot until they get a caption.
      const hasDesc = !!(f.description || '').trim();
      if (hasDesc && f.included_in_context) inContext += 1;
      else excluded += 1;
    }
    return { total: all.length, analysed, placeholder, pending, inContext, excluded };
  }, [figures]);

  if (loading) {
    return (
      <div
        data-testid="stage-1-figures-tab-loading"
        className="border border-zinc-200 rounded-sm bg-white p-6 text-sm text-zinc-500 flex items-center gap-2"
      >
        <Loader2 size={14} className="animate-spin" />
        Loading figures…
      </div>
    );
  }

  if (!figures || figures.length === 0) {
    return (
      <div
        data-testid="stage-1-figures-tab"
        className="space-y-4"
      >
        <div
          data-testid="figures-empty"
          className="border border-dashed border-zinc-300 rounded-sm bg-white p-10 text-center"
        >
          <ImageIcon size={32} className="text-zinc-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-zinc-700 mb-1">
            No figures extracted from this document.
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed max-w-md mx-auto">
            Upload a document containing images to see them here. Figures
            extracted from the IDF (drawings, screenshots, equations rendered
            as images) will appear as thumbnails so you can spot-check what
            the pipeline saw.
          </p>
        </div>
      </div>
    );
  }

  const unanalysedCount = counts.pending + counts.placeholder;
  const batchRunning = !!batchProgress;

  return (
    <div data-testid="stage-1-figures-tab" className="space-y-4">
      <div
        data-testid="figures-header"
        className="flex flex-wrap items-center justify-between gap-3 border border-zinc-200 rounded-sm bg-white px-4 py-2.5"
      >
        <div className="flex items-center gap-2 text-xs text-zinc-600">
          <ImageIcon size={13} className="text-[#002FA7]" />
          <span data-testid="figures-header-counts">
            <strong className="text-zinc-900">{counts.total}</strong> figures ·{' '}
            <strong className="text-zinc-900">{counts.analysed}</strong> analysed ·{' '}
            <strong className="text-zinc-900">{unanalysedCount}</strong> not analysed
          </span>
          <span
            data-testid="figures-context-counts"
            className="text-zinc-400 hidden sm:inline"
          >
            ·{' '}
            <strong className="text-green-700">{counts.inContext}</strong> in context ·{' '}
            <strong className="text-zinc-700">{counts.excluded}</strong> excluded
          </span>
          {refreshing && <Loader2 size={12} className="animate-spin text-zinc-400" />}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="figures-refresh"
            onClick={refresh}
            disabled={refreshing || batchRunning}
            className="bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-2 py-1 text-[11px] font-medium transition-colors flex items-center gap-1 disabled:opacity-50"
          >
            <RefreshCcw size={11} /> Refresh
          </button>
          <button
            type="button"
            data-testid="figures-analyse-all"
            disabled={batchRunning || isFrozen || unanalysedCount === 0}
            onClick={handleAnalyseAll}
            title={
              unanalysedCount === 0
                ? 'Every figure already has a description'
                : 'Run vision over each unanalysed figure, one at a time'
            }
            className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-2.5 py-1 text-[11px] font-medium transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {batchRunning
              ? <Loader2 size={11} className="animate-spin" />
              : <Sparkles size={11} />}
            {batchRunning
              ? `Analysing ${batchProgress.current} of ${batchProgress.total}…`
              : 'Analyse All'}
          </button>
          <button
            type="button"
            data-testid="figures-discard-unanalysed"
            disabled={isFrozen || batchRunning || counts.pending === 0}
            onClick={async () => {
              if (isFrozen) return;
              const targets = (figures || []).filter((f) => _figureBucket(f) === 'pending');
              for (const t of targets) {
                try {
                  // eslint-disable-next-line no-await-in-loop
                  await api.deleteMatterFigure(matterId, t.id);
                } catch {
                  // Best effort — surface the first failure via onError
                  // but keep going so partial cleanup still happens.
                }
              }
              await refresh();
            }}
            className="bg-white text-red-700 border border-red-200 hover:bg-red-50 rounded-sm px-2 py-1 text-[11px] font-medium transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 size={11} /> Discard All Unanalysed
          </button>
        </div>
      </div>

      {batchProgress && (
        <div
          data-testid="figures-batch-progress"
          className="flex items-center justify-between gap-3 border border-blue-200 bg-blue-50 rounded-sm px-3 py-2 text-xs text-[#002FA7]"
        >
          <div className="flex items-center gap-2">
            <Loader2 size={12} className="animate-spin" />
            <span>
              Analysing figure <strong>{batchProgress.current}</strong> of{' '}
              <strong>{batchProgress.total}</strong>
              {batchProgress.failed > 0 && (
                <span className="text-amber-700"> · {batchProgress.failed} failed</span>
              )}
            </span>
          </div>
          <span className="text-[10px] text-zinc-500">
            Sequential to respect rate limits
          </span>
        </div>
      )}

      {undoState && (
        <div
          data-testid="figures-undo-bar"
          className="flex items-center justify-between gap-3 border border-amber-200 bg-amber-50 rounded-sm px-3 py-2 text-xs text-amber-900"
        >
          <span>Discarded {undoState.figure.fig_number}.</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="figures-undo"
              onClick={handleUndo}
              className="inline-flex items-center gap-1 text-amber-900 hover:text-amber-700 underline-offset-2 hover:underline"
            >
              <Undo2 size={11} /> Undo
            </button>
            <button
              type="button"
              data-testid="figures-undo-dismiss"
              onClick={() => setUndoState(null)}
              className="text-amber-700 hover:text-amber-900"
              aria-label="Dismiss undo"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Per-document grouping. We bucket on document_id so figures
          extracted from the primary IDF and a supplementary PDF
          render under separate headers. The bucket order matches
          first-appearance in `figures` (which is already sorted by
          sequence_num at the backend), so the IDF group lands on top
          when its earliest figure precedes the supplementary doc's.
          Figures with a null document_id (orphaned by an older
          migration) bucket into a final "Unknown source" group. */}
      {(() => {
        type Group = {
          documentId: number | 'unknown';
          documentName: string;
          documentType: string | null;
          items: typeof figures;
        };
        const groups: Group[] = [];
        const indexByKey: Record<string, number> = {};
        for (const f of figures) {
          const key = f.document_id == null ? 'unknown' : String(f.document_id);
          if (indexByKey[key] === undefined) {
            indexByKey[key] = groups.length;
            groups.push({
              documentId: f.document_id ?? 'unknown',
              documentName: f.document_name || (
                f.document_id == null
                  ? 'Unknown source'
                  : `Document #${f.document_id}`
              ),
              documentType: f.document_type ?? null,
              items: [],
            });
          }
          groups[indexByKey[key]].items.push(f);
        }
        const showHeaders = groups.length > 1
          || (groups.length === 1 && groups[0].documentId !== 'unknown');

        return groups.map((group) => (
          <div
            key={String(group.documentId)}
            data-testid={`figures-group-${group.documentId}`}
            className="space-y-2"
          >
            {showHeaders && (
              <div
                data-testid={`figures-group-header-${group.documentId}`}
                className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-zinc-500 border-b border-zinc-200 pb-1"
              >
                <ImageIcon size={11} className="text-zinc-400" />
                <span className="text-zinc-700 font-medium normal-case">
                  {group.documentName}
                </span>
                {group.documentType && group.documentType !== 'idf' && (
                  <span className="text-[10px] text-zinc-400 normal-case">
                    ({group.documentType})
                  </span>
                )}
                <span className="ml-1 text-zinc-400 normal-case">
                  · {group.items.length} figure{group.items.length === 1 ? '' : 's'}
                </span>
              </div>
            )}
            <div
              data-testid={`figures-grid-${group.documentId}`}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
            >
              {group.items.map((f) => {
          const bucket = _figureBucket(f);
          const isPlaceholder = bucket === 'placeholder';
          const description = (f.description || '').trim();
          const hasDescription = !!description;
          const isAnalysed = bucket === 'analysed' || (isPlaceholder && hasDescription);
          const fullUrl = f.url; // backend serves /api/matter/.../image
          const isDeleting = !!pendingDelete[f.id];
          const isAnalysing = !!analysing[f.id];
          const failed = !!analyseFailed[f.id];
          const expanded = !!expandedDesc[f.id];

          // Long-description toggle: collapse anything over the
          // truncate threshold so the grid doesn't grow into a
          // giraffe column when one figure has a wall-of-text caption.
          const shouldClamp = description.length > DESCRIPTION_TRUNCATE_AT;
          const visibleDescription = shouldClamp && !expanded
            ? description.slice(0, DESCRIPTION_TRUNCATE_TO).trimEnd() + '…'
            : description;

          // Excluded === has a description but the drafter has flipped
          // included_in_context off. We don't grey out figures without
          // a description (their checkbox is irrelevant — they aren't
          // in the context block either way) so the visual treatment
          // here is "purposefully held out", not "broken".
          const isExcluded = hasDescription && !f.included_in_context;
          const togglePending = !!pendingToggle[f.id];

          let analyseLabel: string;
          if (isAnalysing) analyseLabel = 'Analysing…';
          else if (failed) analyseLabel = 'Analysis failed — retry';
          else if (hasDescription) analyseLabel = 'Re-analyse';
          else analyseLabel = 'Analyse';

          // Re-analyse uses muted styling so a fresh, never-analysed
          // figure is visually the primary action. Failed retries
          // stay primary-coloured to draw the eye back to them.
          const analyseClass = failed
            ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
            : hasDescription
              ? 'bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50'
              : 'bg-[#002FA7] text-white hover:bg-[#002277] border border-[#002FA7]';

          return (
            <div
              key={f.id}
              data-testid={`figure-card-${f.id}`}
              className="border border-zinc-200 rounded-sm bg-white overflow-hidden flex flex-col"
            >
              <div className="relative">
                <a
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`figure-thumbnail-${f.id}`}
                  className="block bg-zinc-50 border-b border-zinc-100 aspect-[4/3] flex items-center justify-center hover:bg-zinc-100 transition-colors overflow-hidden"
                  title="Open full-size in new tab"
                >
                  {isPlaceholder && !f.has_preview ? (
                    // True placeholder path: source is an EMF/WMF
                    // vector AND the backend has no side-car PNG
                    // (container lacks LibreOffice, or conversion
                    // failed). Keep the amber block + tooltip so
                    // the drafter knows why the thumbnail is missing.
                    <div
                      className="flex flex-col items-center justify-center text-zinc-400 text-[11px] text-center p-3"
                      title={'This figure is in EMF/WMF vector format. Install LibreOffice on the server for full preview support.'}
                    >
                      <AlertTriangle size={20} className="text-amber-500 mb-1" />
                      <div className="font-mono uppercase tracking-wider">
                        {(f.mime_type || 'unknown').replace(/^image\//, '')}
                      </div>
                      <div className="mt-0.5">vector — placeholder shown</div>
                    </div>
                  ) : (
                    // Real image path: either a natively-viewable
                    // format, or a vector with a preview PNG
                    // rendered at ingest. The backend serves the
                    // preview transparently via the same URL when
                    // present, so the <img> just works.
                    <img
                      src={fullUrl}
                      alt={f.fig_number}
                      loading="lazy"
                      className={`max-h-full max-w-full object-contain ${isExcluded ? 'grayscale opacity-60' : ''}`}
                    />
                  )}
                </a>
                {isPlaceholder && f.has_preview && (
                  // Subtle informational label when we're showing a
                  // converted PNG — the drafter should know the
                  // thumbnail is a render, not the literal source,
                  // so a round-trip mismatch (e.g. LibreOffice
                  // dropping a non-standard EMF record) doesn't
                  // surprise them later.
                  <div
                    data-testid={`figure-converted-label-${f.id}`}
                    className="absolute top-1 right-1 pointer-events-none"
                  >
                    <span className="inline-block bg-white/90 border border-amber-200 rounded-sm px-1.5 py-0.5 text-[9px] font-medium text-amber-700 shadow-sm">
                      vector — converted to PNG
                    </span>
                  </div>
                )}
                {isExcluded && (
                  <div
                    data-testid={`figure-excluded-overlay-${f.id}`}
                    className="absolute inset-0 bg-zinc-500/15 pointer-events-none flex items-end justify-center pb-2"
                  >
                    <span className="inline-flex items-center gap-1 bg-white/90 border border-zinc-300 rounded-sm px-2 py-0.5 text-[10px] font-medium text-zinc-700 shadow-sm">
                      <EyeOff size={10} /> Excluded from context
                    </span>
                  </div>
                )}
              </div>

              <div className="px-3 pt-2 pb-2 border-b border-zinc-100">
                <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">
                  {f.fig_number}
                </div>
                <div
                  data-testid={`figure-status-${f.id}`}
                  className="flex items-center gap-1.5 text-[11px] mt-0.5"
                >
                  {isAnalysed ? (
                    <>
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500" aria-hidden />
                      <span className="text-green-700 font-medium">Analysed</span>
                      {isPlaceholder && !f.has_preview && (
                        // Only flag "from placeholder" when there's
                        // no real render — a preview PNG means the
                        // caption is based on actual pixels, not a
                        // label placeholder, so the warning is stale.
                        <span className="text-amber-700">— from placeholder</span>
                      )}
                    </>
                  ) : isPlaceholder && !f.has_preview ? (
                    <>
                      <span className="inline-block w-2 h-2 rounded-full bg-amber-500" aria-hidden />
                      <span className="text-amber-700 font-medium">Placeholder</span>
                      <span className="text-zinc-500">— vision can only describe position</span>
                    </>
                  ) : (
                    <>
                      <span className="inline-block w-2 h-2 rounded-full bg-zinc-300 border border-zinc-400" aria-hidden />
                      <span className="text-zinc-500">Not analysed</span>
                    </>
                  )}
                </div>
                {hasDescription && (
                  <div
                    data-testid={`figure-description-${f.id}`}
                    className="mt-2 text-[11px] text-zinc-700 leading-snug bg-zinc-50 border border-zinc-100 rounded-sm px-2 py-1.5"
                  >
                    <p className="whitespace-pre-wrap">{visibleDescription}</p>
                    {shouldClamp && (
                      <button
                        type="button"
                        data-testid={`figure-description-toggle-${f.id}`}
                        onClick={() => setExpandedDesc((prev) => ({ ...prev, [f.id]: !expanded }))}
                        className="mt-1 text-[10px] text-[#002FA7] hover:underline"
                      >
                        {expanded ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                )}
                {hasDescription && (
                  <label
                    data-testid={`figure-include-toggle-${f.id}`}
                    className="mt-2 flex items-center gap-1.5 text-[11px] cursor-pointer select-none text-zinc-700 hover:text-zinc-900"
                    title="When checked, this figure's caption flows into the IDF context block used by Stage 1 scoring + downstream prompts"
                  >
                    <input
                      type="checkbox"
                      data-testid={`figure-include-checkbox-${f.id}`}
                      checked={!!f.included_in_context}
                      onChange={(e) => handleToggleInclude(f, e.target.checked)}
                      disabled={isFrozen || togglePending || isAnalysing || batchRunning}
                      className="h-3 w-3 rounded-sm border-zinc-300 text-[#002FA7] focus:ring-[#002FA7] disabled:opacity-50"
                    />
                    <span>
                      {f.included_in_context ? 'Include in IDF context' : 'Excluded from IDF context'}
                    </span>
                    {togglePending && <Loader2 size={10} className="animate-spin text-zinc-400" />}
                  </label>
                )}
              </div>

              <div className="px-3 py-2 flex items-center gap-2">
                <button
                  type="button"
                  data-testid={`figure-analyse-${f.id}`}
                  onClick={() => analyseFigure(f.id)}
                  disabled={isAnalysing || batchRunning || isFrozen}
                  title={
                    isFrozen
                      ? 'Stage is frozen'
                      : hasDescription ? 'Re-run vision and overwrite the existing description' : 'Run vision over this figure'
                  }
                  className={`flex-1 rounded-sm px-2 py-1 text-[11px] font-medium transition-colors flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed ${analyseClass}`}
                >
                  {isAnalysing
                    ? <Loader2 size={11} className="animate-spin" />
                    : hasDescription
                      ? <RotateCcw size={11} />
                      : <Sparkles size={11} />}
                  {analyseLabel}
                </button>
                <button
                  type="button"
                  data-testid={`figure-discard-${f.id}`}
                  onClick={() => handleDiscard(f)}
                  disabled={isFrozen || isDeleting || isAnalysing || batchRunning}
                  className="bg-white text-red-700 border border-red-200 hover:bg-red-50 rounded-sm px-2 py-1 text-[11px] font-medium transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                  Discard
                </button>
              </div>
            </div>
          );
              })}
            </div>
          </div>
        ));
      })()}

    </div>
  );
}
